import { type getInputProps, type getSelectProps, useInputControl } from "@conform-to/react"
import { useControl } from "@conform-to/react/future"
import { format, parseISO } from "date-fns"
import { useId, useRef } from "react"
import { NumberFormatBase, useNumericFormat } from "react-number-format"
import { cn } from "#app/utils/misc.tsx"
import { Checkbox, type CheckboxProps } from "./ui/checkbox"
import { Icon } from "./ui/icon"
import { Calendar } from "./ui2/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./ui2/popover"
import { SelectContent, SelectItem, SelectTrigger, SelectValue, Select as ShadcnSelect } from "./ui2/select"

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
    id,
    errors,
}: {
    errors?: ListOfErrors
    id?: string
}) {
    const errorsToRender = errors?.filter(Boolean)
    if (!errorsToRender?.length) return null
    return (
        <ul id={id} className="flex flex-col gap-1">
            {errorsToRender.map((e) => (
                <li key={e} className="text-foreground-destructive text-[10px]">
                    {e}
                </li>
            ))}
        </ul>
    )
}

export function Field({
    labelProps,
    inputProps,
    errors,
    className,
}: {
    labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
    inputProps: React.InputHTMLAttributes<HTMLInputElement>
    errors?: ListOfErrors
    className?: string
}) {
    const fallbackId = useId()
    const id = inputProps.id ?? fallbackId
    const errorId = errors?.length ? `${id}-error` : undefined
    return (
        <div className={className}>
            <div
                className={cn(
                    "h-10 w-full relative",

                    "has-[[data-slot=input]:disabled]:pointer-events-none",
				    "has-[[data-slot=input]:disabled]:opacity-50",

                    "hover:[&>[data-slot=border]]:border-primary",

                    "has-[[data-slot=input]:focus]:[&>[data-slot=border]]:border-2",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=border]]:border-ring",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:text-xs",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:text-ring",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:top-[0.5px]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:right-auto",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=cover]]:h-[3px]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=cover]]:top-[1px]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:text-xs",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:top-[0.5px]",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:right-auto",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",
                    
                    "has-[[data-slot=input][aria-invalid=true]]:[&>[data-slot=label]]:text-input-invalid",
                    "has-[[data-slot=input][aria-invalid=true]]:[&>[data-slot=border]]:border-input-invalid",
                )}
            >            
                <div 
                    data-slot="border"
                    className="absolute inset-0 border border-input rounded-md"
                />
                <div
                    data-slot="cover"
                    className="h-[2px] max-w-0 absolute top-[0.5px] -translate-y-1/2 left-2 text-xs truncate bg-background"
                >
                    {labelProps?.children ? <span className="invisible px-1">{labelProps.children}</span> : null}
                </div>
                <label
                    htmlFor={id}
                    data-slot="label"
                    className="absolute top-1/2 -translate-y-1/2 left-2 right-1 px-1 md:text-sm truncate text-muted-foreground transition-all ease-out duration-200"
                >
                        {labelProps?.children}
                </label>
                <input
                    id={id}
                    aria-invalid={errorId ? true : undefined}
                    aria-describedby={errorId}
                    data-slot="input"
                    placeholder=" "
                    {...inputProps}
                    className="outline-none absolute inset-0 pl-3 pr-1 md:text-sm [&:-webkit-autofill]:[-webkit-text-fill-color:var(--color-foreground)] [&:-webkit-autofill]:[transition:background-color_9999s_ease-out]"
                />
            </div>
            <div className="px-4 pt-1">
                {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
            </div>
        </div>
    )
}

export function SelectField({
    labelProps,
    selectProps,
    items,
    errors,
    className
}: {
    labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
    selectProps: ReturnType<typeof getSelectProps> & {
        value?: string
        disabled?: boolean
        autoFocus?: boolean
        className?: string
        onChange?: (value: any) => void
    }
    items: Array<{ name: string; value: string; disabled?: boolean }>
    errors?: ListOfErrors
    className?: string
}) {
    const selectRef = useRef<React.ElementRef<typeof SelectTrigger>>(null);
    const control = useInputControl({
        key: selectProps.key,
        name: selectProps.name,
        formId: selectProps.form,
        initialValue: typeof selectProps.defaultValue === "string" ? selectProps.defaultValue : undefined
    })
    
    const fallbackId = useId()
    const id = selectProps.id ?? fallbackId
    const errorId = errors?.length ? `${id}-error` : undefined
    return (
        <div className={className}>
            <input
                name={selectProps.name}
                defaultValue={selectProps.defaultValue}
                className="sr-only"
                tabIndex={-1}
                onFocus={() => selectRef.current?.focus()}
            />
			<ShadcnSelect
				value={selectProps.value?.toString() ?? control.value ?? ""}
				onValueChange={value => {
					control.change(value)
                    selectProps.onChange?.(value)
				}}
				onOpenChange={(open) => {
					if (!open) {
						control.blur();
					}
				}}
			>
                <SelectTrigger
                    ref={selectRef}
                    aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					onBlur={control.blur}
                    disabled={selectProps.disabled}
                    autoFocus={selectProps.autoFocus}
                    className={cn(
                        "outline-none block text-left h-10 w-full relative",
                
                        "disabled:pointer-events-none",
                        "disabled:opacity-50",
                
                        "hover:[&>[data-slot=border]]:border-primary",

                        "focus:[&>[data-slot=border]]:border-2",
                        "focus:[&>[data-slot=border]]:border-ring",
                        "focus:[&>[data-slot=label]]:text-xs",
                        "focus:[&>[data-slot=label]]:text-ring",
                        "focus:[&>[data-slot=label]]:top-[0.5px]",
                        "focus:[&>[data-slot=label]]:right-auto",
                        "focus:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                        "focus:[&>[data-slot=cover]]:h-[3px]",
                        "focus:[&>[data-slot=cover]]:top-[1px]",
                        "focus:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                        "data-[state=open]:[&>[data-slot=border]]:border-2",
                        "data-[state=open]:not-aria-[invalid]:[&>[data-slot=border]]:border-ring",
                        "data-[state=open]:[&>[data-slot=label]]:text-xs",
                        "data-[state=open]:not-aria-[invalid]:[&>[data-slot=label]]:text-ring",
                        "data-[state=open]:[&>[data-slot=label]]:top-[0.5px]",
                        "data-[state=open]:[&>[data-slot=label]]:right-auto",
                        "data-[state=open]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                        "data-[state=open]:[&>[data-slot=cover]]:h-[3px]",
                        "data-[state=open]:[&>[data-slot=cover]]:top-[1px]",
                        "data-[state=open]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                        "not-data-[placeholder]:[&>[data-slot=label]]:text-xs",
                        "not-data-[placeholder]:[&>[data-slot=label]]:top-[0.5px]",
                        "not-data-[placeholder]:[&>[data-slot=label]]:right-auto",
                        "not-data-[placeholder]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                        "not-data-[placeholder]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                        "data-[placeholder]:[&_[data-slot=value]]:invisible",

                        "aria-[invalid]:[&>[data-slot=label]]:text-input-invalid",
                        "aria-[invalid]:[&>[data-slot=border]]:border-input-invalid",
                        selectProps.className
                    )}
                >
                    <div
                        data-slot="border"
                        className="absolute inset-0 border border-input rounded-md"
                    />
                    <div
                        data-slot="cover"
                        className="h-[2px] max-w-0 absolute top-[0.5px] -translate-y-1/2 left-2 text-xs truncate bg-background"
                    >
                        <span className="invisible px-1">{labelProps.children}</span>
                    </div>
                    <label
                        htmlFor={id}
                        data-slot="label"
                        className="absolute top-1/2 -translate-y-1/2 left-2 right-7 px-1 md:text-sm truncate text-muted-foreground transition-all ease-out duration-200"
                    >
                            {labelProps.children}
                    </label>
                    <div data-slot="value" className="absolute top-1/2 -translate-y-1/2 left-2 right-7 px-1 md:text-sm truncate"><SelectValue placeholder={labelProps.children} /></div>
				</SelectTrigger>
				<SelectContent className="max-h-40">
					{items.map((item) => {
						return (
							<SelectItem key={item.value} value={item.value} disabled={item.disabled ?? false}>
								{item.name}
							</SelectItem>
						);
					})}
				</SelectContent>
			</ShadcnSelect>
            <div className="px-4 pt-1">
                {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
            </div>
        </div>
    )
}

export function DatePickerField({
    labelProps,
    inputProps,
    calendarProps,
    errors,
    className
}: {
    labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
    inputProps: ReturnType<typeof getInputProps> & {
        formatValue?: (date: Date) => string
    }
    calendarProps?: {
        required?: boolean
        modifiers?: React.ComponentProps<typeof Calendar>["modifiers"]
    }
    errors?: ListOfErrors
    className?: string
}) {
    const triggerRef = useRef<HTMLButtonElement>(null)

    const control = useInputControl({
        key: inputProps.key,
        name: inputProps.name,
        formId: inputProps.form,
        initialValue: inputProps.defaultValue
    })

    const fallbackId = useId()
    const id = inputProps.id ?? fallbackId
    const errorId = errors?.length ? `${id}-error` : undefined
    return (
        <div className={className}>
            <input
                name={inputProps.name}
                defaultValue={inputProps.defaultValue}
                className="sr-only"
                tabIndex={-1}
                onFocus={() => triggerRef.current?.focus()}
            />
            <Popover
                onOpenChange={(open) => {
                    if (!open) {
                        control.blur();
                    }
                }}
            >
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        ref={triggerRef}
                        aria-invalid={errorId ? true : undefined}
					    aria-describedby={errorId}
                        className={cn(
                            "outline-none block text-left h-10 w-full relative",

                            "disabled:pointer-events-none",
                            "disabled:opacity-50",
                    
                            "hover:[&>[data-slot=border]]:border-primary",

                            "focus:[&>[data-slot=border]]:border-2",
                            "focus:[&>[data-slot=border]]:border-ring",
                            "focus:[&>[data-slot=label]]:text-xs",
                            "focus:[&>[data-slot=label]]:text-ring",
                            "focus:[&>[data-slot=label]]:top-[0.5px]",
                            "focus:[&>[data-slot=label]]:right-auto",
                            "focus:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                            "focus:[&>[data-slot=cover]]:h-[3px]",
                            "focus:[&>[data-slot=cover]]:top-[1px]",
                            "focus:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                            "data-[state=open]:[&>[data-slot=border]]:border-2",
                            "data-[state=open]:not-aria-[invalid]:[&>[data-slot=border]]:border-ring",
                            "data-[state=open]:[&>[data-slot=label]]:text-xs",
                            "data-[state=open]:not-aria-[invalid]:[&>[data-slot=label]]:text-ring",
                            "data-[state=open]:[&>[data-slot=label]]:top-[0.5px]",
                            "data-[state=open]:[&>[data-slot=label]]:right-auto",
                            "data-[state=open]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                            "data-[state=open]:[&>[data-slot=cover]]:h-[3px]",
                            "data-[state=open]:[&>[data-slot=cover]]:top-[1px]",
                            "data-[state=open]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                            control.value && "[&>[data-slot=label]]:text-xs",
                            control.value && "[&>[data-slot=label]]:top-[0.5px]",
                            control.value && "[&>[data-slot=label]]:right-auto",
                            control.value && "[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                            control.value && "[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                            "aria-[invalid]:[&>[data-slot=label]]:text-input-invalid",
                            "aria-[invalid]:[&>[data-slot=border]]:border-input-invalid",
                        )}
                    >
                        <div
                            data-slot="border"
                            className="absolute inset-0 border border-input rounded-md"
                        />
                        <div
                            data-slot="cover"
                            className="h-[2px] max-w-0 absolute top-[0.5px] -translate-y-1/2 left-2 text-xs truncate bg-background"
                        >
                            <span className="invisible px-1">{labelProps.children}</span>
                        </div>
                        <label
                            htmlFor={id}
                            data-slot="label"
                            className="absolute top-1/2 -translate-y-1/2 left-2 right-7 px-1 md:text-sm truncate text-muted-foreground transition-all ease-out duration-200"
                        >
                            {labelProps.children}
                        </label>
                        <div data-slot="value" className="absolute top-1/2 -translate-y-1/2 left-2 right-7 px-1 md:text-sm truncate">
                            {control.value ? inputProps.formatValue?.(parseISO(control.value)) ?? format(parseISO(control.value), 'PPP') : undefined}
                        </div>
                        <Icon name="calendar" className="h-4 w-4 absolute top-1/2 -translate-y-1/2 right-2 opacity-50" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-auto p-0"
                    align="start"
                >
                    <Calendar
                        mode="single"
                        navLayout="around"
                        captionLayout="dropdown"
                        selected={control.value ? parseISO(control.value) : new Date('')}
                        defaultMonth={control.value ? parseISO(control.value) : new Date()}
                        onSelect={(value) => control.change(value ? format(value, "yyyy-MM-dd") : '')}
                        {...calendarProps}
                    />
                </PopoverContent>
            </Popover>
            <div className="px-4 pt-1">
                {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
            </div>
        </div>
    )
}

export function CurrencyField({
    labelProps,
    inputProps,
    isNegative,
    errors,
    className,
}: {
    labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
    inputProps: React.InputHTMLAttributes<HTMLInputElement>
    isNegative: boolean
    errors?: ListOfErrors
    className?: string
}) {
    const inputRef = useRef<HTMLInputElement>(null)
    const control = useControl({
        defaultValue: inputProps.defaultValue?.toString(),
        onFocus() {
            inputRef.current?.focus();
        },
    })
    const prefix = isNegative ? "-$" : "$"
    const { getCaretBoundary, format, ...rest } = useNumericFormat({
        thousandSeparator: ',',
        decimalScale: 2,
        fixedDecimalScale: true,
        prefix,
        allowNegative: false,
        allowLeadingZeros: false
    })

    const fallbackId = useId()
    const id = inputProps.id ?? fallbackId
    const errorId = errors?.length ? `${id}-error` : undefined
    return (
        <div className={className}>
            <input name={inputProps.name} ref={control.register} tabIndex={-1} className="sr-only" />
            <div
                className={cn(
                    "h-10 w-full relative",

                    "has-[[data-slot=input]:disabled]:pointer-events-none",
				    "has-[[data-slot=input]:disabled]:opacity-50",

                    "hover:[&>[data-slot=border]]:border-primary",

                    "has-[[data-slot=input]:focus]:[&>[data-slot=border]]:border-2",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=border]]:border-ring",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:text-xs",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:text-ring",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:top-[0.5px]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:right-auto",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=cover]]:h-[3px]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=cover]]:top-[1px]",
                    "has-[[data-slot=input]:focus]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",

                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:text-xs",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:top-[0.5px]",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:right-auto",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=label]]:max-w-[calc(100%-var(--spacing)*4)]",
                    "has-[[data-slot=input]:not(:placeholder-shown)]:[&>[data-slot=cover]]:max-w-[calc(100%-var(--spacing)*4)]",
                    
                    "has-[[data-slot=input][aria-invalid=true]]:[&>[data-slot=label]]:text-input-invalid",
                    "has-[[data-slot=input][aria-invalid=true]]:[&>[data-slot=border]]:border-input-invalid",
                    inputProps.className
                )}
            >
                <div
                    data-slot="border"
                    className="absolute inset-0 border border-input rounded-md"
                />
                <div
                    data-slot="cover"
                    className="h-[2px] max-w-0 absolute top-[0.5px] -translate-y-1/2 left-2 text-xs truncate bg-background"
                >
                    <span className="invisible px-1">{labelProps.children}</span>
                </div>
                <label
                    htmlFor={id}
                    data-slot="label"
                    className="absolute top-1/2 -translate-y-1/2 left-2 right-1 px-1 md:text-sm truncate text-muted-foreground transition-all ease-out duration-200"
                >
                        {labelProps.children}
                </label>
                <NumberFormatBase
                    id={id}
                    aria-invalid={errorId ? true : undefined}
                    aria-describedby={errorId}
                    data-slot="input"
                    disabled={inputProps.disabled}
                    format={(numStr) => {
                        if (format) {
                            if (numStr == '.' || numStr ==  "0") {
                                return format("0.00")
                            }

                            if (numStr.startsWith("0") && numStr.charAt(1) !== ".") {
                                return format(numStr.substring(1))
                            }
                                
                            return format(numStr)
                        }
                        return numStr
                    }}
                    {...rest}
                    inputMode='decimal'
                    placeholder=" "
                    className={cn(
                        "outline-none absolute inset-0 pl-3 pr-1 md:text-sm [&:-webkit-autofill]:[-webkit-text-fill-color:var(--color-foreground)] [&:-webkit-autofill]:[transition:background-color_9999s_ease-out]",
                    )}
                    getInputRef={inputRef}
                    value={inputProps.value?.toString() ?? control.value ?? ""}
                    onValueChange={value => {
                        control.change(value.formattedValue)
                    }}
                    onBlur={() => control.blur()}
                />
            </div>
            <div className="px-4 pt-1">
                {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
            </div>            
        </div>
    )
}

export function CheckboxField({
    labelProps,
    buttonProps,
    errors,
    className,
}: {
    labelProps: React.ComponentProps<'label'>
    buttonProps: CheckboxProps & {
        name: string
        form: string
        value?: string
    }
    errors?: ListOfErrors
    className?: string
}) {
    const { key, defaultChecked, ...checkboxProps } = buttonProps
    const fallbackId = useId()
    const checkedValue = buttonProps.value ?? 'on'
    const input = useInputControl({
        key,
        name: buttonProps.name,
        formId: buttonProps.form,
        initialValue: defaultChecked ? checkedValue : undefined,
    })
    const id = buttonProps.id ?? fallbackId
    const errorId = errors?.length ? `${id}-error` : undefined

    return (
        <div className={className}>
            <div className="flex items-center gap-2">
                <Checkbox
                    {...checkboxProps}
                    id={id}
                    aria-invalid={errorId ? true : undefined}
                    aria-describedby={errorId}
                    checked={input.value === checkedValue}
                    onCheckedChange={(state) => {
                        input.change(state.valueOf() ? checkedValue : '')
                        buttonProps.onCheckedChange?.(state)
                    }}
                    onFocus={(event) => {
                        input.focus()
                        buttonProps.onFocus?.(event)
                    }}
                    onBlur={(event) => {
                        input.blur()
                        buttonProps.onBlur?.(event)
                    }}
                    type="button"
                />
                <label
                    htmlFor={id}
                    {...labelProps}
                    className={cn(
                        "text-body-xs text-muted-foreground self-center",
                        labelProps.className
                    )}
                />
            </div>
            <div className="px-4 pt-1">
                {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
            </div>
        </div>
    )
}